import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, Lock, Clock, Info, Pencil, AlertTriangle, CheckCircle2, X, CheckCircle, Loader2, FilePlus } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import SetBillingModelDialog from "./SetBillingModelDialog";
import AmendmentDialog from "./AmendmentDialog";
import PaymentPlanBreakdown from "./PaymentPlanBreakdown";

// =====================================================================
// BillingStateCard — Tx Plan billing snapshot.
// =====================================================================
// Reads dos_v2.get_plan_billing_state(plan_id). Per Cameron's spec
// (2026-05-07) the card now surfaces:
//
//   - Plan total / Paid / Remaining (unchanged)
//   - Billing model w/ "Set billing model" button (until the parent
//     charge locks at Visit 1)
//   - Visit math: X of Y visits done
//   - Suggested next-visit payment (silent default — staff override
//     freely, no warning modal per spec)
//   - Final-visit hard block hint when only 1 visit remains AND
//     balance > 0 (the actual gate is enforced server-side by
//     close_plan; this is just the on-screen heads-up)
// =====================================================================

const PRESET_LABELS = {
  full:      { label: "Full upfront",   desc: "100% on Visit 1" },
  "50_50":   { label: "50 / 50",        desc: "Half on Visit 1, half on the final visit" },
  per_phase: { label: "Even split",     desc: "Plan total split evenly across visits" },
  custom:    { label: "Custom",         desc: "Down payment then even split for the rest" },
};

function formatPHP(centavos) {
  if (!Number.isFinite(centavos)) return "—";
  const pesos = Math.round(centavos / 100);
  return `₱${pesos.toLocaleString()}`;
}

function buildDemoBillingState(current, result = {}) {
  const total = Number(current?.final_total_centavos ?? current?.total_centavos ?? current?.plan_total_items_centavos ?? 0);
  const totalVisits = Math.max(1, Number(current?.total_visits || 1));
  const preset = result.preset || current?.preset || null;
  const customDp = result.custom_schedule?.dp_centavos ?? result.dp_centavos ?? null;
  const suggested = preset === "full"
    ? total
    : preset === "50_50"
      ? Math.round(total / 2)
      : preset === "per_phase"
        ? Math.round(total / totalVisits)
        : preset === "custom"
          ? Number(customDp || 0)
          : null;

  return {
    ...(current || {}),
    preset,
    custom_schedule: preset === "custom"
      ? {
          dp_centavos: Number(customDp || 0),
          ...(result.custom_schedule?.min_per_visit_centavos != null
            ? { min_per_visit_centavos: Number(result.custom_schedule.min_per_visit_centavos) } : {}),
          ...(result.custom_schedule?.num_payments != null
            ? { num_payments: Number(result.custom_schedule.num_payments) } : {}),
        }
      : null,
    suggested_next_centavos: suggested,
  };
}

export default function BillingStateCard({
  planId,
  onChanged,
  demoMode = false,
  demoState = null,
  onDemoStateChange,
  // Optional patient + plan_code metadata — only used to label the
  // amendment dialog. Safe to omit; the dialog falls back to "this
  // plan" when missing.
  patientName,
  planCode,
  // Clinic's Settings-enabled billing models + default (single source of
  // truth). Omit → the picker falls back to all models / standard default.
  enabledPresets,
  defaultPreset,
}) {
  const [state, setState]       = useState(null);
  const [error, setError]       = useState(null);
  const [loading, setLoading]   = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Close-plan recovery dialog. Opens when the receptionist clicks
  // "Close plan" — useful when:
  //   * All visits done but the post-checkout auto-close failed
  //     (items rolled up async, or ar_reason was needed and the
  //     receptionist dismissed the dialog earlier).
  //   * Receptionist wants to formally close a 0-balance plan from
  //     the patient chart (no checkout flow).
  // For balance > 0 the dialog requires an AR reason; for balance 0
  // it's a one-click confirm.
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closeReason, setCloseReason] = useState("");
  const [closingPlan, setClosingPlan] = useState(false);
  // Amendment dialog. The "Add amendment" button on the card opens it.
  // Available when the plan is Accepted (per spec — drafts edit
  // originals directly; Completed/Cancelled is locked).
  const [amendmentOpen, setAmendmentOpen] = useState(false);

  const refetch = useCallback(() => {
    if (!planId) return Promise.resolve();
    if (demoMode) {
      setState(demoState || null);
      setError(null);
      setLoading(false);
      return Promise.resolve();
    }
    setLoading(true);
    setError(null);
    return supabase.rpc("get_plan_billing_state", { p_plan_id: planId }).then(({ data, error: e }) => {
      setLoading(false);
      if (e) {
        setError(e.message ?? "Could not read billing state.");
        return;
      }
      setState(data);
    });
  }, [planId, demoMode, demoState]);

  useEffect(() => {
    let cancelled = false;
    refetch().catch(() => { /* error already in state */ });
    return () => { cancelled = true; };
  }, [refetch]);

  if (!planId) return null;
  if (loading && !state) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-4 animate-pulse">
        <div className="h-3 w-24 bg-slate-200 rounded mb-3" />
        <div className="h-6 w-40 bg-slate-200 rounded" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-800">
        Couldn't read billing state — {error}
      </div>
    );
  }
  if (!state) return null;

  const total      = Number(state.total_centavos) || 0;
  const paid       = Number(state.paid_centavos) || 0;
  const remaining  = Number(state.remaining_centavos) || 0;
  // plan_total_items_centavos was added by the Step C migration. If
  // we're talking to an older backend it'll be missing — fall back to
  // total so we don't render a misleading ₱0.
  const planTotalItems = Number(state.plan_total_items_centavos) || total;
  // Three totals from the amendments migration. Fall back when an
  // older backend hasn't been migrated yet — original = planTotalItems,
  // amendments = 0.
  const originalEstimate = state.original_estimate_centavos != null
    ? Number(state.original_estimate_centavos)
    : planTotalItems;
  const amendmentsTotal  = state.amendments_centavos != null
    ? Number(state.amendments_centavos)
    : 0;
  const finalTotal = state.final_total_centavos != null
    ? Number(state.final_total_centavos)
    : planTotalItems;
  const hasAmendments = amendmentsTotal !== 0;

  const preset     = state.preset || null;
  const preLabel   = preset ? (PRESET_LABELS[preset] || { label: preset, desc: "" }) : null;
  const locked     = !!state.charge_locked;
  const planStatus = state.plan_status || null;
  const needsBillingModel = !locked && preset == null;
  const liveStatuses = new Set(["Accepted", "Needs Booking", "Scheduled", "In Progress", "AR"]);
  // Amendments only on live revenue-pipeline plans. Drafts edit
  // originals directly; lost/terminal states are locked.
  const canAmend   = liveStatuses.has(planStatus);

  const totalVisits     = Number(state.total_visits) || 0;
  const completedVisits = Number(state.completed_visits) || 0;
  const remainingVisits = Number(state.remaining_visits) || 0;
  const isFirstVisit    = !!state.is_first_visit_pending;
  const isFinalVisit    = !!state.is_final_visit_pending;
  const requiresClearing = !!state.requires_clearing;
  const suggested       = state.suggested_next_centavos != null ? Number(state.suggested_next_centavos) : null;
  const customDp        = state.custom_schedule?.dp_centavos != null
    ? Number(state.custom_schedule.dp_centavos) : null;
  const customMin       = state.custom_schedule?.min_per_visit_centavos != null
    ? Number(state.custom_schedule.min_per_visit_centavos) : null;
  const customNumPayments = state.custom_schedule?.num_payments != null
    ? Number(state.custom_schedule.num_payments) : null;

  const paidPct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;

  // Show "next visit" guidance only when there's a preset AND visits remain.
  const showNextHint = preset != null && remainingVisits > 0;

  // Show "Close plan" recovery action when every planned visit is
  // marked done but the plan hasn't been formally closed yet (status
  // != Completed). This is the manual escape hatch for the post-
  // checkout auto-close failure path: receptionist sees the plan with
  // 2/2 visits done but pipeline_stage still "In Progress", and can
  // click the button to retry. balance > 0 → dialog asks for AR
  // reason; balance = 0 → one-click confirm.
  const allVisitsDone = totalVisits > 0 && completedVisits === totalVisits;
  const showCloseAction = allVisitsDone && planStatus !== "Completed";

  async function performClose(reason) {
    setClosingPlan(true);
    const { error: closeErr } = await supabase.rpc("close_plan", {
      p_plan_id:   planId,
      p_ar_reason: reason || null,
    });
    setClosingPlan(false);
    if (closeErr) {
      toast.error(`Couldn't close plan: ${closeErr.message}`);
      return false;
    }
    toast.success(reason ? "Plan closed and routed to AR." : "Plan closed.");
    setCloseDialogOpen(false);
    setCloseReason("");
    refetch();
    onChanged?.();
    return true;
  }

  const billingModelSummary = (
    <div
      className={[
        "rounded-lg border px-3 py-2 flex items-start gap-2",
        needsBillingModel
          ? "bg-amber-50 border-amber-300 ring-1 ring-amber-200"
          : "bg-slate-50 border-slate-200",
      ].join(" ")}
      data-testid={needsBillingModel ? "billing-model-required-card" : "billing-model-summary-card"}
    >
      {needsBillingModel ? (
        <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" strokeWidth={2.25} />
      ) : (
        <Info className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2 flex-wrap">
          <p className={`text-xs font-semibold ${needsBillingModel ? "text-amber-950" : "text-slate-800"}`}>
            {needsBillingModel ? (
              "Set the billing model before payment"
            ) : (
              <>
                Billing model:
                <span className="ml-1 font-semibold">{preLabel.label}</span>
              </>
            )}
          </p>
          {!locked ? (
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              data-testid="billing-set-model"
              data-tutorial-id="checkout-tx-plan-set-billing-model"
              className={[
                "inline-flex items-center gap-1 rounded-md text-[11px] font-semibold",
                needsBillingModel
                  ? "bg-amber-600 px-2.5 py-1.5 text-white hover:bg-amber-700"
                  : "text-teal-700 hover:text-teal-900",
              ].join(" ")}
            >
              <Pencil className="w-3 h-3" />
              {preset == null ? "Set billing model" : "Change"}
            </button>
          ) : null}
        </div>
        {needsBillingModel ? (
          <p className="text-[11px] text-amber-900 mt-1">
            Visit 1 cannot continue to payment until this is set. Choose how the patient pays the accepted Tx Plan.
          </p>
        ) : null}
        {preLabel?.desc ? (
          <p className="text-[11px] text-slate-500 mt-0.5">{preLabel.desc}</p>
        ) : null}
        {preset === "custom" && customDp != null ? (
          <p className="text-[11px] text-slate-500 mt-0.5">
            Down payment: <strong className="tabular-nums">{formatPHP(customDp)}</strong>
          </p>
        ) : null}
        {!locked && preset != null ? (
          <p className="text-[11px] text-slate-500 mt-1">
            Locks when the patient checks out for Visit 1.
          </p>
        ) : null}
      </div>
    </div>
  );

  return (
    <>
      <div
        className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 space-y-3"
        data-testid="billing-state-card"
        data-tutorial-id="checkout-tx-plan-billing-card"
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="inline-flex items-center gap-2">
            <Wallet className="w-4 h-4 text-slate-500" />
            <h3 className="text-sm font-semibold text-slate-900">Billing</h3>
          </div>
          <div className="inline-flex items-center gap-1.5">
            {locked ? (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200"
                    data-testid="billing-state-locked">
                <Lock className="w-3 h-3" /> Locked
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 ring-1 ring-amber-200"
                    data-testid="billing-state-pending">
                <Clock className="w-3 h-3" /> Not started
              </span>
            )}
          </div>
        </div>

        {billingModelSummary}

        {preset != null ? (
          <PaymentPlanBreakdown
            preset={preset}
            totalCentavos={locked ? total : planTotalItems}
            visitCount={totalVisits}
            dpCentavos={customDp}
            minPerVisitCentavos={customMin}
            numPayments={customNumPayments}
            data-testid="billing-plan-breakdown"
          />
        ) : null}

        <div className="grid grid-cols-3 gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Plan total</p>
            <p className="text-base font-semibold text-slate-900 tabular-nums" data-testid="billing-total">
              {formatPHP(locked ? total : planTotalItems)}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Paid</p>
            <p className="text-base font-semibold text-emerald-700 tabular-nums" data-testid="billing-paid">
              {formatPHP(paid)}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Remaining</p>
            <p className={`text-base font-semibold tabular-nums ${remaining > 0 ? "text-slate-900" : "text-emerald-700"}`}
               data-testid="billing-remaining">
              {formatPHP(remaining)}
            </p>
          </div>
        </div>

        {/* Three-total breakdown — Original Estimate / Amendments /
            Final Total (per Cameron's spec). Hidden when there are
            no amendments — single-line "Plan total" above already
            says it. Once an amendment lands, the breakdown surfaces
            so the patient/staff can see what changed and why. */}
        {hasAmendments ? (
          <div className="rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2 space-y-1 text-[12px]"
               data-testid="billing-amendment-breakdown">
            <div className="flex items-baseline justify-between">
              <span className="text-slate-600">Original estimate</span>
              <span className="font-medium tabular-nums text-slate-800">{formatPHP(originalEstimate)}</span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-slate-600">Amendments</span>
              <span className={[
                "font-medium tabular-nums",
                amendmentsTotal < 0 ? "text-rose-700" : "text-amber-700",
              ].join(" ")}>
                {amendmentsTotal > 0 ? "+" : ""}{formatPHP(amendmentsTotal)}
              </span>
            </div>
            <div className="border-t border-slate-200 mt-1 pt-1 flex items-baseline justify-between">
              <span className="font-semibold text-slate-900">Final total</span>
              <span className="font-bold tabular-nums text-slate-900">{formatPHP(finalTotal)}</span>
            </div>
          </div>
        ) : null}

        {locked && total > 0 ? (
          <div className="space-y-1">
            <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all"
                   style={{ width: `${paidPct}%` }}
                   aria-label={`${paidPct}% paid`} />
            </div>
            <p className="text-[11px] text-slate-500 tabular-nums">{paidPct}% collected</p>
          </div>
        ) : null}

        {/* Visit math + final-visit hint */}
        {totalVisits > 0 ? (
          <div className="text-[11px] text-slate-600 flex items-center gap-2 flex-wrap" data-testid="billing-visit-math">
            <span className="tabular-nums">
              <strong>{completedVisits}</strong> of <strong>{totalVisits}</strong> visit{totalVisits === 1 ? "" : "s"} done
            </span>
            {isFirstVisit ? (
              <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold bg-blue-100 text-blue-800">First visit pending</span>
            ) : null}
            {isFinalVisit ? (
              <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold bg-amber-100 text-amber-800">Final visit pending</span>
            ) : null}
          </div>
        ) : null}

        {/* Add amendment — the canonical surface for treatment-related
            changes on an Accepted plan (procedure ran long, additional
            materials, complications, mid-treatment discount, etc.).
            Per spec, this is the ONLY UI affordance for changing what
            an Accepted plan bills. Drafts edit originals directly;
            Completed/Cancelled is locked. */}
        {canAmend ? (
          <div className="rounded-lg border border-teal-200 bg-teal-50/40 px-3 py-2 flex items-center gap-2">
            <FilePlus className="w-3.5 h-3.5 text-teal-700 shrink-0" strokeWidth={2.25} />
            <div className="flex-1 min-w-0 text-[12px] text-teal-900">
              <p className="font-semibold">Need to adjust the bill?</p>
              <p className="text-[11px] text-teal-800/80">
                Add an amendment — extended chair time, extra materials, complication, mid-treatment discount.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setAmendmentOpen(true)}
              data-testid="billing-add-amendment"
              className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-[11px] font-semibold rounded text-white bg-teal-600 hover:bg-teal-700"
            >
              <FilePlus className="w-3.5 h-3.5" strokeWidth={2.25} />
              Add amendment
            </button>
          </div>
        ) : null}

        {/* Close plan recovery — shown when every visit is done but
            the plan didn't formally close. Receptionist's manual
            escape hatch. */}
        {showCloseAction ? (
          <div
            className={[
              "rounded-lg border px-3 py-2 flex items-center gap-2 text-[12px]",
              remaining > 0
                ? "bg-amber-50 border-amber-200 text-amber-900"
                : "bg-emerald-50 border-emerald-200 text-emerald-900",
            ].join(" ")}
            data-testid="billing-close-action"
          >
            {remaining > 0 ? (
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" strokeWidth={2.25} />
            ) : (
              <CheckCircle className="w-3.5 h-3.5 shrink-0" strokeWidth={2.25} />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-semibold">
                {remaining > 0
                  ? `All visits done — ₱${Math.round(remaining / 100).toLocaleString()} balance still open.`
                  : "All visits done — ready to close."}
              </p>
              <p className="text-[11px] mt-0.5">
                {remaining > 0
                  ? "Close the plan to formally route the shortfall to AR with a reason."
                  : "Close the plan to mark it Completed."}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setCloseReason("");
                if (remaining > 0) setCloseDialogOpen(true);
                else performClose(null);
              }}
              disabled={closingPlan}
              data-testid="billing-close-plan"
              className={[
                "shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-[11px] font-semibold rounded text-white",
                remaining > 0
                  ? "bg-amber-600 hover:bg-amber-700"
                  : "bg-emerald-600 hover:bg-emerald-700",
                closingPlan ? "opacity-60 cursor-wait" : "",
              ].join(" ")}
            >
              {closingPlan ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
              Close plan
            </button>
          </div>
        ) : null}

        {/* Suggested next payment — silent default per spec, no warning modal */}
        {showNextHint ? (
          suggested != null ? (
            <div
              className={[
                "rounded-lg border px-3 py-2 flex items-start gap-2 text-[12px]",
                requiresClearing
                  ? "bg-amber-50 border-amber-200 text-amber-900"
                  : "bg-teal-50 border-teal-200 text-teal-900",
              ].join(" ")}
              data-testid="billing-suggested"
            >
              {requiresClearing ? (
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" strokeWidth={2.25} />
              ) : (
                <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" strokeWidth={2.25} />
              )}
              <div className="min-w-0 flex-1">
                <p>
                  {isFirstVisit ? "Suggested for Visit 1: " :
                   isFinalVisit ? "Final visit must clear: " :
                   "Suggested for next visit: "}
                  <strong className="tabular-nums">{formatPHP(suggested)}</strong>
                </p>
                {requiresClearing ? (
                  <p className="text-[11px] mt-0.5 text-amber-800/80">
                    Shortfalls route to Accounts Receivable when the plan closes — staff can record an AR reason at close-out.
                  </p>
                ) : (
                  <p className="text-[11px] mt-0.5 text-teal-800/80">
                    Pre-fill at checkout; staff can override freely.
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600 flex items-start gap-2"
                 data-testid="billing-suggested-none">
              <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>
                {preset === "50_50"
                  ? "50 / 50 has no required payment between Visit 1 and the final visit. Payments are accepted any time and reduce the final balance."
                  : "No suggested amount for this visit."}
              </span>
            </div>
          )
        ) : null}
      </div>

      {/* AR-reason dialog for closing a non-zero-balance plan. Mirrors
          the post-checkout ArRouteDialog pattern: receptionist types a
          short reason, clicks Confirm, close_plan() records the reason
          and the plan moves to Completed. */}
      {closeDialogOpen ? (
        <div
          role="dialog" aria-modal="true"
          data-testid="billing-close-dialog"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
          onMouseDown={(e) => { if (e.target === e.currentTarget && !closingPlan) setCloseDialogOpen(false); }}
        >
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600" strokeWidth={2.25} />
                  Close plan with balance
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Outstanding: <strong className="text-amber-700 tabular-nums">{formatPHP(remaining)}</strong>
                </p>
              </div>
              <button type="button" onClick={() => !closingPlan && setCloseDialogOpen(false)}
                className="p-1 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-40">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-3">
              <p className="text-[12px] text-slate-700">
                Type a short reason and we'll route the shortfall to AR. Staff can collect later from the ledger.
              </p>
              <textarea
                value={closeReason}
                onChange={(e) => setCloseReason(e.target.value)}
                rows={3}
                placeholder="e.g. Patient settling next month — promised by Friday"
                data-testid="billing-close-reason"
                className="w-full px-3 py-2 text-sm rounded border border-slate-300 focus:border-teal-500 outline-none resize-none"
              />
            </div>
            <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center gap-3">
              <button type="button" onClick={() => setCloseDialogOpen(false)}
                disabled={closingPlan}
                className="ml-auto text-xs font-semibold text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded">
                Cancel
              </button>
              <button type="button"
                onClick={() => {
                  const reason = closeReason.trim();
                  if (!reason) { toast.error("Type a short reason so the AR ledger has context."); return; }
                  performClose(reason);
                }}
                disabled={closingPlan}
                data-testid="billing-close-confirm"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded disabled:opacity-60">
                {closingPlan ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
                Route to AR &amp; close plan
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <SetBillingModelDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        planId={planId}
        planTotalCentavos={locked ? total : planTotalItems}
        planVisitCount={totalVisits}
        currentPreset={preset}
        currentDpCentavos={customDp}
        currentMinPerVisitCentavos={customMin}
        currentNumPayments={customNumPayments}
        enabledPresets={enabledPresets}
        defaultPreset={defaultPreset}
        demoMode={demoMode}
        onSaved={(result) => {
          if (demoMode) {
            const next = buildDemoBillingState(state, result);
            setState(next);
            onDemoStateChange?.(next);
            onChanged?.();
            return;
          }
          // Refresh THIS card's local snapshot AND tell the parent to
          // refetch its own cached billing state. Without onChanged the
          // parent keeps its stale planBillingState — surfaced as the
          // "Custom DP ₱20k but Bill still shows 50/50 ₱43.5k" mismatch
          // Cameron flagged at chairside checkout.
          refetch();
          onChanged?.();
        }}
      />

      <AmendmentDialog
        open={amendmentOpen}
        onOpenChange={setAmendmentOpen}
        planId={planId}
        planCode={planCode}
        patientName={patientName}
        onSaved={() => {
          // Same idempotent refresh: card refetches its billing
          // snapshot (three totals reflect the new amendment) and the
          // parent (TxPlanBuilder) reloads the plan so the items list
          // shows the new is_amendment row.
          refetch();
          onChanged?.();
        }}
      />
    </>
  );
}
